import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  UserCheck,
  Ban,
  ClipboardList,
  Eye,
  Database,
  ArrowRight,
} from 'lucide-react';
import { Card } from './ui/card';

/**
 * "Jak HussarAi wpisuje się w AI Act?" — institutional companion section
 * displayed on the homepage and on /ai-act-polska. Five doctrinal cards
 * + one CTA to the 30-day Compliance Operator pilot.
 *
 * Safety: never claims guaranteed AI Act compliance. Uses "AI Act-ready
 * workflows" / "supports human oversight" language only.
 */
const AIActSection = ({
  variant = 'home',
  ctaHref = '/operator-zgodnosci',
}) => {
  const { t } = useTranslation();

  const cards = [
    {
      key: 'human-oversight',
      icon: <UserCheck className="w-7 h-7" />,
      titleKey: 'aiAct.cards.humanOversight.title',
      bodyKey: 'aiAct.cards.humanOversight.body',
    },
    {
      key: 'no-auto-decisions',
      icon: <Ban className="w-7 h-7" />,
      titleKey: 'aiAct.cards.noAutoDecisions.title',
      bodyKey: 'aiAct.cards.noAutoDecisions.body',
    },
    {
      key: 'audit-trail',
      icon: <ClipboardList className="w-7 h-7" />,
      titleKey: 'aiAct.cards.auditTrail.title',
      bodyKey: 'aiAct.cards.auditTrail.body',
    },
    {
      key: 'explainable',
      icon: <Eye className="w-7 h-7" />,
      titleKey: 'aiAct.cards.explainable.title',
      bodyKey: 'aiAct.cards.explainable.body',
    },
    {
      key: 'retention',
      icon: <Database className="w-7 h-7" />,
      titleKey: 'aiAct.cards.retention.title',
      bodyKey: 'aiAct.cards.retention.body',
    },
  ];

  return (
    <section
      className="hussar-section hussar-aiact-section"
      data-testid={`aiact-section-${variant}`}
    >
      <div className="hussar-container">
        <p className="hussar-section-eyebrow" data-testid="aiact-eyebrow">
          {t('aiAct.eyebrow')}
        </p>
        <h2 className="hussar-section-title" data-testid="aiact-heading">
          {t('aiAct.heading')}
        </h2>
        <p
          className="hussar-section-subtitle"
          data-testid="aiact-subheading"
          style={{ maxWidth: 760 }}
        >
          {t('aiAct.subheading')}
        </p>

        <div
          className="hussar-aiact-grid"
          data-testid="aiact-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 16,
            marginTop: 28,
          }}
        >
          {cards.map((card) => (
            <Card
              key={card.key}
              className="hussar-aiact-card"
              data-testid={`aiact-card-${card.key}`}
              style={{ padding: 22 }}
            >
              <div
                className="hussar-aiact-card-icon"
                aria-hidden="true"
              >
                {card.icon}
              </div>
              <h3
                style={{ fontSize: 17, fontWeight: 600, margin: '0 0 6px' }}
                data-testid={`aiact-card-${card.key}-title`}
              >
                {t(card.titleKey)}
              </h3>
              <p
                style={{ fontSize: 14, lineHeight: 1.55, margin: 0 }}
                data-testid={`aiact-card-${card.key}-body`}
              >
                {t(card.bodyKey)}
              </p>
            </Card>
          ))}
        </div>

        <div
          className="hussar-aiact-cta-row"
          data-testid="aiact-cta-row"
          style={{
            marginTop: 28,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 14,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <p
            style={{ fontSize: 13, opacity: 0.7, margin: 0, maxWidth: 560 }}
            data-testid="aiact-safety-note"
          >
            {t('aiAct.safetyNote')}
          </p>
          <Link
            to={ctaHref}
            data-testid="aiact-pilot-cta"
            className="hussar-btn-primary"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 20px',
              borderRadius: 8,
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            {t('aiAct.pilotCta')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default AIActSection;
